import { useState } from "react";
import { useGetTopics } from "@/api/useGetTopics";
import { Badge, Button, Flex, Table, Text } from "@radix-ui/themes";
import { Search } from "@/components/shared/inputs/search";
import { Topic } from "@/v2/entities/topic/Topic.model";
import { PlusIcon } from "@radix-ui/react-icons";
import { Loader2Icon } from "lucide-react";
import { TopicItem } from "./TopicItem";
import { CreateTopicDialog } from "./CreateTopicDialog";

export function Topics() {
  const { data, isLoading, refetch } = useGetTopics();
  const [searchPhrase, setSearchPhrase] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  if (isLoading) {
    return (
      <Flex justify={"center"} align={"center"} height={"100%"}>
        <Loader2Icon className="animate-spin" />
      </Flex>
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex items-center p-4 border-b">
        <h3 style={{ fontWeight: "500" }}>Topics</h3>
      </div>

      <div className="flex-1 px-8 py-8">
        <Flex direction={"column"} gap={"8"}>
          <Flex
            direction={"row"}
            gap={"2"}
            justify={"between"}
            align={"center"}
          >
            <Flex direction={"row"} gap={"2"} align={"center"}>
              <Flex gap={"2"} align={"center"}>
                <Text size={"3"} weight={"medium"}>
                  Topics
                </Text>
                <Badge color="gray">{data?.length}</Badge>
              </Flex>
              <Search
                initialValue={searchPhrase}
                placeholder="Search topics"
                setSearchPhrase={setSearchPhrase}
                debounce={50}
              />
            </Flex>
            <Button
              color="gray"
              variant="solid"
              highContrast
              onClick={() => setCreateDialogOpen(true)}
            >
              <PlusIcon />
              Create Topic
            </Button>
          </Flex>

          <Table.Root>
            <Table.Header>
              <Table.Row className="text-gray-500 font-normal">
                <Table.ColumnHeaderCell className="font-normal w-1/4">
                  Topic Name
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className="font-normal w-2/3">
                  Description
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className="font-normal w-[80px] text-right">
                  Actions
                </Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {data?.length === 0 && (
                <Table.Row>
                  <Table.Cell colSpan={3}>
                    <Flex
                      align="center"
                      justify="center"
                      py="6"
                      direction="column"
                      gap="2"
                    >
                      <Text size="2" color="gray">
                        No topics found
                      </Text>
                      <Text size="2" color="gray">
                        Create a new topic to get started
                      </Text>
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              )}
              {data
                ?.sort((a: { topic: string }, b: { topic: string }) => {
                  return a.topic
                    .toLowerCase()
                    .localeCompare(b.topic.toLowerCase());
                })
                ?.filter(
                  (topic: Topic) =>
                    topic.topic
                      .toLowerCase()
                      .includes(searchPhrase.toLowerCase()) ||
                    (topic.description &&
                      topic.description
                        .toLowerCase()
                        .includes(searchPhrase.toLowerCase()))
                )
                .map((topic: Topic) => (
                  <TopicItem
                    key={topic.id}
                    topic={topic}
                    refetch={refetch}
                    allTopics={data}
                  />
                ))}
            </Table.Body>
          </Table.Root>
        </Flex>
      </div>

      <CreateTopicDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        refetch={refetch}
      />
    </div>
  );
}
